import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getServiceId } from '@/api/tokens.helper';
import { getErrorStatus } from '@/common/error';

import { createTag, getTags } from './tags.api';
import type { EventTag } from './tags.mappers';

/** The tags the current vessel can put on its events — the options for the chips and the filter. */
export const useEventTagsQuery = (enabled = true) => {
  const vesselId = getServiceId();
  return useQuery({
    queryKey: ['tags', vesselId],
    queryFn: getTags,
    enabled: enabled && !!vesselId,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Creates a vessel tag, or — when the name is taken (409) — hands back the
 * existing tag of that name, so typing "guests" just picks Guests.
 */
export const useCreateTagMutation = () => {
  const queryClient = useQueryClient();
  const vesselId = getServiceId();
  return useMutation({
    mutationFn: async (name: string): Promise<EventTag> => {
      try {
        return await createTag(name);
      } catch (error) {
        if (getErrorStatus(error) !== 409) throw error;
        const tags = await queryClient.fetchQuery({
          queryKey: ['tags', vesselId],
          queryFn: getTags,
        });
        const existing = tags.find((tag) => tag.name.toLowerCase() === name.toLowerCase());
        if (!existing) throw error;
        return existing;
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tags', vesselId] }),
  });
};
